// The apps, wherever someone looks for them (spec "Get the apps"): the dashboard's "Use cmx everywhere" row, its
// sheet (from the row, the account menu and Signed-in devices) and the guide's "Apps and downloads". One block,
// five apps: Cloud Machine for Windows, Mac and Android, cmx on the phone, the cmx CLI. This device's app comes
// first; a tile says where it's signed in already (the hub names each device's app, CLIDevice.client).

import { useEffect, useId, useState, useSyncExternalStore, type ReactNode } from 'react';
import { IconBrandAndroid, IconBrandApple, IconBrandWindows, IconCircleCheckFilled, IconDeviceMobile, IconDownload, IconQrcode, IconTerminal2, type Icon } from '@tabler/icons-react';
import { cli, installCommand, type CliDevice } from '../api';
import { onAndroid, thisDevice, type Device } from '../device';
import { Link } from '../router';
import { store } from '../store';
import { APPS } from './AppDownload';
import { buttonClass } from './Button';
import { CodeRow, CopyButton } from './CodeRow';
import { Dialog } from './Dialog';
import { Segmented } from './ds';
import { QR } from './QR';
import { agoPhrase, secondsSince } from './time';
import './get-apps.css';

export type AppKind = 'windows' | 'mac' | 'android' | 'iphone' | 'cli';
const KINDS: AppKind[] = ['windows', 'mac', 'android', 'iphone', 'cli'];
/** The apps with a download of their own: shown once it's published. */
const DOWNLOADS = ['windows', 'mac', 'android'] as const;
type DownloadKind = (typeof DOWNLOADS)[number];
const isDownload = (k: AppKind): k is DownloadKind => (DOWNLOADS as readonly AppKind[]).includes(k);

type Fact = [string, ReactNode];
const META: Record<AppKind, { name: string; title: string; icon: Icon; here: string; needs: string; facts: Fact[] }> = {
  windows: {
    name: 'Windows',
    title: 'Cloud Machine for Windows',
    icon: IconBrandWindows,
    here: 'This PC',
    needs: 'Windows 10 (21H2) or 11, 64-bit',
    facts: [
      ['Needs', 'Windows 10 (21H2) or Windows 11, 64-bit. No admin rights.'],
      ['Updates', 'Installs them by itself while the app is in the background.'],
      ['Uninstall', 'Settings › Apps › Installed apps › Cloud Machine › Uninstall.'],
    ],
  },
  mac: {
    name: 'Mac',
    title: 'Cloud Machine for Mac',
    icon: IconBrandApple,
    here: 'This Mac',
    needs: 'macOS 26 or later',
    facts: [
      ['Needs', 'macOS 26 or later, Apple silicon or Intel.'],
      ['Updates', 'Installs them by itself while the app is in the background (Settings › App).'],
      ['Uninstall', 'Quit Cloud Machine and drag it from Applications to the Trash.'],
    ],
  },
  android: {
    name: 'Android',
    title: 'Cloud Machine for Android',
    icon: IconBrandAndroid,
    here: 'This phone',
    needs: 'Android 8 or later',
    facts: [
      ['Needs', 'Android 8 or later, on a 64-bit phone or tablet.'],
      ['Updates', 'The app offers each update; one tap installs it.'],
      ['Uninstall', 'Touch and hold Cloud Machine, then Uninstall.'],
    ],
  },
  iphone: {
    name: 'Phone',
    title: 'cmx on your phone',
    icon: IconDeviceMobile,
    here: 'This phone',
    needs: 'In the browser, added to your Home Screen',
    facts: [
      ['Needs', 'Safari on iPhone or iPad; Chrome on Android.'],
      ['Updates', 'Always the latest: it’s the web app.'],
      ['Remove', 'Touch and hold its icon, then Remove App.'],
      ['iPhone app', 'A native app is in beta on TestFlight.'],
    ],
  },
  cli: {
    name: 'Terminal',
    title: 'The cmx CLI',
    icon: IconTerminal2,
    here: 'This computer',
    needs: 'macOS, Linux, or WSL on Windows',
    facts: [
      ['Needs', 'macOS or Linux (x64 or arm64); on Windows, WSL. The Mac and Windows apps install it for you.'],
      ['Updates', 'Updates itself in the background.'],
      [
        'Uninstall',
        <>
          <code>cmx logout</code>, then delete <code>~/.local/bin/cmx</code>.
        </>,
      ],
    ],
  },
};

/** An app is offered once its download is published (/api/me apps; all of them before /api/me answers). */
const offered = (k: AppKind) => {
  const apps = store.get().me?.apps;
  return !isDownload(k) || !apps || apps[k];
};

const BY_DEVICE: Record<Device, AppKind> = { windows: 'windows', mac: 'mac', mobile: 'iphone', linux: 'cli' };
/** The app this device would want (device.ts: an Android phone its app once published, another phone or tablet cmx
 *  in the browser, Linux the CLI). */
export const hereApp = (): AppKind => (thisDevice() === 'mobile' && onAndroid() && offered('android') ? 'android' : BY_DEVICE[thisDevice()]);
/** A phone or tablet: nothing to scan a code with but itself. */
const onPhone = () => thisDevice() === 'mobile';

/** The apps there are to get (the Mac, Windows and Android apps once published), this device's first. */
export function appOrder(): AppKind[] {
  const all = KINDS.filter(offered);
  const here = hereApp();
  return all.includes(here) ? [here, ...all.filter((k) => k !== here)] : all;
}

// ---- the sheet: opened from anywhere, shown once (AppsSheetHost, in DashLayout) ----

let sheet: AppKind | null = null;
const listeners = new Set<() => void>();
const setSheet = (k: AppKind | null) => {
  sheet = k;
  listeners.forEach((l) => l());
};
/** Opens the apps sheet on one app (this device's when none is named). */
export const openApps = (k: AppKind = appOrder()[0]) => setSheet(k);

export function AppsSheetHost() {
  const open = useSyncExternalStore(
    (l) => {
      listeners.add(l);
      return () => listeners.delete(l);
    },
    () => sheet,
  );
  if (!open) return null;
  return (
    <Dialog title="Get the apps" wide onClose={() => setSheet(null)} testId="apps-sheet">
      <AppTabs value={open} onChange={setSheet} idPrefix="apps-sheet" />
      <AppGuide kind={open} />
    </Dialog>
  );
}

function AppTabs({ value, onChange, idPrefix }: { value: AppKind; onChange: (k: AppKind) => void; idPrefix: string }) {
  return (
    <Segmented
      label="Apps"
      value={value}
      onChange={onChange}
      tabs={{ controls: `${idPrefix}-panel`, idPrefix }}
      items={appOrder().map((k) => ({ value: k, label: META[k].name, icon: META[k].icon, testid: `${idPrefix}-${k}` }))}
    />
  );
}

type Step = { title: string; detail: ReactNode };
const phoneUrl = () => `${location.origin}/login`;

/** One app: a card to get it (its tile, its name, what it needs, then the download, the code or the command) and the
 *  steps after, in one tray; with `facts`, what it needs, how it updates and how it goes, in the next (the guide). */
export function AppGuide({ kind, facts = false, panelId = 'apps-sheet-panel' }: { kind: AppKind; facts?: boolean; panelId?: string }) {
  const m = META[kind];
  const stepsId = useId();
  const phone = onPhone();
  const download = isDownload(kind);
  const steps: Step[] = download
    ? [...(kind === 'android' && !phone ? [{ title: 'Scan the code', detail: 'With your Android phone’s camera; the download opens in the browser' }] : []), ...APPS[kind].steps]
    : kind === 'iphone'
      ? [
          ...(phone ? [] : [{ title: 'Scan the code', detail: 'With your phone’s camera; cmx opens in the browser' }]),
          { title: 'Sign in', detail: 'With the account you use here' },
          { title: 'Add it to your Home Screen', detail: 'Safari: Share › Add to Home Screen. It opens full screen, like an app' },
        ]
      : [
          { title: 'Run it in your terminal', detail: 'macOS, Linux, or WSL on Windows' },
          { title: 'Approve the sign-in', detail: 'A browser tab opens on this computer; one click' },
          {
            title: 'Run cmx',
            detail: (
              <>
                Your sessions, in your terminal. <Link to="/guide#cli">All commands</Link>
              </>
            ),
          },
        ];
  const meta = download
    ? `${m.needs} · Free with your account`
    : kind === 'iphone'
      ? 'In the browser, with the keys a phone keyboard lacks'
      : `${m.needs} · Free with your account`;
  return (
    <div className="apps-guide" id={panelId} role="tabpanel" data-testid={`apps-guide-${kind}`}>
      <div className="apps-tray">
        <div className="apps-get" data-kind={kind}>
          <span className="apps-get-tile" aria-hidden="true">
            <m.icon size={26} stroke={1.75} />
          </span>
          <span className="apps-get-text">
            <b>{m.title}</b>
            <span>{meta}</span>
          </span>
          {kind === 'android' && !onAndroid() ? (
            !phone && <ScanQr {...ANDROID_QR} />
          ) : download ? (
            <a className={buttonClass('primary', 'md', 'apps-get-action')} href={APPS[kind].href} download={APPS[kind].file} data-testid={`start-download-${kind}`}>
              <IconDownload size={16} stroke={1.75} aria-hidden="true" />
              {APPS[kind].label}
            </a>
          ) : kind === 'iphone' ? (
            !phone && <ScanQr {...PHONE_QR} />
          ) : (
            <div className="apps-get-cmd">
              <CodeRow text={installCommand()} codeTestid="apps-cli-cmd" copyTestid="apps-cli-copy" />
            </div>
          )}
        </div>
        <p className="apps-tray-head apps-tray-head-steps" id={stepsId}>
          {download ? 'Installing Cloud Machine' : kind === 'iphone' ? 'Setting up cmx on your phone' : 'Setting up the cmx CLI'}
        </p>
        <ol className="apps-steps" aria-labelledby={stepsId} data-testid={download ? `${kind}-install-steps` : undefined}>
          {steps.map((s, i) => (
            <li key={s.title}>
              <span className="apps-step-title">
                <span className="apps-step-n" aria-hidden="true">
                  {i + 1}
                </span>
                {s.title}
              </span>
              <span className="apps-step-detail">{s.detail}</span>
            </li>
          ))}
        </ol>
      </div>
      {facts && (
        <div className="apps-tray">
          <p className="apps-tray-head" id={`${panelId}-facts`}>
            Details
          </p>
          <dl className="apps-facts" aria-labelledby={`${panelId}-facts`}>
            {factsOf(kind).map(([k, v]) => (
              <div key={k}>
                <dt>{k}</dt>
                <dd>{v}</dd>
              </div>
            ))}
          </dl>
        </div>
      )}
    </div>
  );
}

/** An app's facts; the phone's also point to the Android app once it's published (in the guide: a link to its tab). */
function factsOf(kind: AppKind): Fact[] {
  const facts = META[kind].facts;
  if (kind !== 'iphone' || !offered('android')) return facts;
  return [
    ...facts,
    [
      'Android app',
      <>
        A native app: <a href="#download-android">Cloud Machine for Android</a>.
      </>,
    ],
  ];
}

type QrCode = { url: () => string; title: string; label: string; text: string; testid: string };
const PHONE_QR: QrCode = {
  url: phoneUrl,
  title: 'Open cmx on your phone',
  label: 'QR code that opens cmx on your phone',
  text: 'Point your phone’s camera at the code; cmx opens in the browser. Sign in with the account you use here.',
  testid: 'apps-open-qr',
};
// The Android app's page in the guide: on the phone, its tab with the download first and the steps under it.
const ANDROID_QR: QrCode = {
  url: () => `${location.origin}/guide#download-android`,
  title: 'Get Cloud Machine for Android',
  label: 'QR code that opens the Android app’s download on your phone',
  text: 'Point your Android phone’s camera at the code; the download opens in the browser, with the steps under it.',
  testid: 'apps-open-android-qr',
};

/** "Scan QR code": the code in the shared Dialog, centred over a blurred page (stacked over the sheet when it's in one). */
function ScanQr({ url, title, label, text, testid }: QrCode) {
  const [open, setOpen] = useState(false);
  return (
    <>
      <button type="button" className={buttonClass('primary', 'md', 'apps-get-action')} aria-haspopup="dialog" onClick={() => setOpen(true)} data-testid={testid}>
        <IconQrcode size={16} stroke={1.75} aria-hidden="true" />
        Scan QR code
      </button>
      {open && (
        <Dialog title={title} onClose={() => setOpen(false)} testId="apps-qr-dialog">
          <div className="apps-qr-well">
            <QR text={url()} className="apps-qr" label={label} testid="apps-qr" />
          </div>
          <p>{text}</p>
        </Dialog>
      )}
    </>
  );
}

// ---- the dashboard's row ----

/** Where each app is signed in (the hub's devices, by app). */
function useSignedIn() {
  const [devices, setDevices] = useState<CliDevice[] | null>(null);
  useEffect(() => {
    let live = true;
    void cli.devices().then((r) => live && r.ok && setDevices(r.data.devices ?? []));
    return () => {
      live = false;
    };
  }, []);
  return (k: AppKind) => (devices ?? []).filter((d) => d.client === k).sort((a, b) => (b.lastUsed ?? '').localeCompare(a.lastUsed ?? ''));
}

/** "Faza's MacBook" from "Faza's MacBook (Cloud Machine app)"; a computer once, however many tokens it has. */
const computer = (name: string) => name.replace(/\s*(\(.*\)|· cmx app)\s*$/, '');

function signedInLine(k: AppKind, on: CliDevice[]): string | null {
  if (!on.length) return null;
  const names = [...new Set(on.map((d) => computer(d.name)))];
  if (k === 'cli' && names.length > 1) return `Signed in on ${names.length} computers`;
  const more = names.length > 1 ? ` and ${names.length - 1} more` : '';
  const seen = on[0].lastUsed ? ` · ${agoPhrase(secondsSince(on[0].lastUsed, Date.now()))}` : '';
  return `Signed in on ${names[0]}${more}${seen}`;
}

export function AppsCard() {
  const signedIn = useSignedIn();
  const here = hereApp();
  return (
    <section className="card card-apps" aria-labelledby="apps-title" data-testid="apps-card">
      <div className="card-head">
        <h2 id="apps-title">Use cmx everywhere</h2>
        <Link to="/guide#download" className={buttonClass('link')} data-testid="apps-all">
          All downloads
        </Link>
      </div>
      <ul className="apps-row">
        {appOrder().map((k, i) => {
          const m = META[k];
          const line = signedInLine(k, signedIn(k));
          return (
            <li key={k} className="apps-tile" data-here={k === here || undefined} data-signed-in={!!line || undefined} style={{ ['--i' as string]: i }} data-testid={`apps-tile-${k}`}>
              <div className="apps-tile-head">
                <span className="apps-tile-icon" aria-hidden="true">
                  <m.icon size={18} stroke={1.6} />
                </span>
                <span className="apps-tile-name">
                  <b>{m.name}</b>
                  {k === here && <span className="apps-here">{m.here}</span>}
                </span>
              </div>
              <p className="apps-tile-line">
                {line ? (
                  <>
                    <IconCircleCheckFilled size={14} className="apps-ok" aria-hidden="true" />
                    <span>{line}</span>
                  </>
                ) : (
                  <span className="muted">{m.needs}</span>
                )}
              </p>
              <TileAction kind={k} primary={k === here && !line} />
            </li>
          );
        })}
      </ul>
    </section>
  );
}

/** A tile's one action: this device's download starts at once (the sheet shows its steps), the phone's code
 *  shows in place, the command copies; the rest open the sheet. */
function TileAction({ kind, primary }: { kind: AppKind; primary: boolean }) {
  if (isDownload(kind) && primary) {
    const a = APPS[kind];
    return (
      <a className={buttonClass('primary', 'sm', 'apps-tile-btn')} href={a.href} download={a.file} onClick={() => openApps(kind)} data-testid={`apps-download-${kind}`}>
        Download
      </a>
    );
  }
  if (kind === 'iphone' && !onPhone()) {
    return (
      <button type="button" className="apps-tile-qr" onClick={() => openApps('iphone')} aria-label="Show how to set up cmx on your phone" data-testid="apps-open-iphone">
        <QR text={phoneUrl()} className="apps-qr apps-qr-sm" label="QR code that opens cmx on your phone" testid="apps-tile-qr" />
        <span className="muted small-print">Scan to open</span>
      </button>
    );
  }
  if (kind === 'cli')
    return (
      <div className="apps-tile-cmd">
        <code data-testid="apps-tile-cli-cmd">curl … /install | sh</code>
        <CopyButton text={installCommand()} testid="apps-tile-cli-copy" />
      </div>
    );
  return (
    <button type="button" className={buttonClass(primary ? 'primary' : 'secondary', 'sm', 'apps-tile-btn')} onClick={() => openApps(kind)} data-testid={`apps-open-${kind}`}>
      {kind === 'iphone' ? 'Set up' : 'Get it'}
    </button>
  );
}

// ---- the guide's section ----

/** "Apps and downloads" in the guide: the apps as tabs (#download-windows lands on one), each with its facts. */
export function GuideApps() {
  const fromHash = () => {
    const k = location.hash.replace(/^#download-/, '');
    return (KINDS as string[]).includes(k) ? (k as AppKind) : null;
  };
  const [kind, setKind] = useState<AppKind>(() => fromHash() ?? appOrder()[0]);
  useEffect(() => {
    const follow = () => {
      const k = fromHash();
      if (k) setKind(k);
    };
    addEventListener('hashchange', follow);
    return () => removeEventListener('hashchange', follow);
  }, []);
  return (
    <div className="apps-guide-tabs" data-testid="guide-apps">
      <AppTabs value={kind} onChange={setKind} idPrefix="download" />
      <AppGuide kind={kind} facts panelId="download-panel" />
    </div>
  );
}
